import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ShoppingBag, ShoppingCart } from "lucide-react";
import SpideyLogo from "./SpideyLogo";
import { scrollToId } from "@/lib/format";
import { useCart } from "@/context/CartContext";

const LINKS = [
    { id: "products", label: "La Collection" },
    { id: "features", label: "Les Atouts" },
    { id: "sizing", label: "Guide des Tailles" },
];

export default function Navbar() {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);
    const location = useLocation();
    const { count, setOpen: setCartOpen } = useCart();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 24);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const go = (id) => {
        setOpen(false);
        scrollToId(id);
    };

    return (
        <header
            className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
                scrolled
                    ? "bg-black/80 backdrop-blur-xl border-b border-zinc-800/80"
                    : "bg-transparent border-b border-transparent"
            }`}
        >
            <nav className="max-w-7xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
                <Link
                    to="/"
                    onClick={() => {
                        setOpen(false);
                        if (location.pathname === "/") {
                            if (window.__lenis) window.__lenis.scrollTo(0);
                            else window.scrollTo({ top: 0, behavior: "smooth" });
                        }
                    }}
                    data-testid="logo-home-link"
                >
                    <SpideyLogo />
                </Link>

                <div className="hidden md:flex items-center gap-8">
                    {LINKS.map((l) => (
                        <button
                            key={l.id}
                            onClick={() => go(l.id)}
                            className="text-[13px] font-mono-brand uppercase tracking-[0.18em] text-zinc-400 hover:text-spidey-neon transition-colors"
                            data-testid={`nav-link-${l.id}`}
                        >
                            {l.label}
                        </button>
                    ))}
                    <button
                        onClick={() => go("products")}
                        className="flex items-center gap-2 bg-spidey-red hover:bg-spidey-neon text-white text-[13px] font-bold uppercase tracking-wider px-5 py-2.5 rounded-full transition-all hover:scale-105 hover:shadow-[0_0_25px_rgba(230,0,38,0.5)]"
                        data-testid="nav-buy-button"
                    >
                        <ShoppingBag size={15} />
                        Acheter
                    </button>
                    <button
                        onClick={() => setCartOpen(true)}
                        aria-label="Ouvrir le panier"
                        className="relative text-zinc-300 hover:text-spidey-neon transition-colors p-2"
                        data-testid="nav-cart-button"
                    >
                        <ShoppingCart size={19} />
                        {count > 0 && (
                            <span
                                className="absolute -top-0.5 -right-0.5 bg-spidey-red text-white text-[10px] font-bold min-w-[18px] h-[18px] px-1 rounded-full flex items-center justify-center"
                                data-testid="nav-cart-count"
                            >
                                {count}
                            </span>
                        )}
                    </button>
                </div>

                <div className="md:hidden flex items-center gap-1">
                    <button
                        onClick={() => setCartOpen(true)}
                        aria-label="Ouvrir le panier"
                        className="relative text-white p-2"
                        data-testid="nav-cart-button-mobile"
                    >
                        <ShoppingCart size={20} />
                        {count > 0 && (
                            <span
                                className="absolute top-0 right-0 bg-spidey-red text-white text-[10px] font-bold min-w-[17px] h-[17px] px-1 rounded-full flex items-center justify-center"
                                data-testid="nav-cart-count-mobile"
                            >
                                {count}
                            </span>
                        )}
                    </button>
                    <button
                        className="text-white p-2"
                        onClick={() => setOpen(!open)}
                        aria-label="Menu"
                        data-testid="nav-mobile-toggle"
                    >
                        {open ? <X size={22} /> : <Menu size={22} />}
                    </button>
                </div>
            </nav>

            {open && (
                <div className="md:hidden bg-black/95 backdrop-blur-xl border-b border-zinc-800/80 px-5 pb-5 pt-1 flex flex-col gap-4">
                    {LINKS.map((l) => (
                        <button
                            key={l.id}
                            onClick={() => go(l.id)}
                            className="text-left text-sm font-mono-brand uppercase tracking-[0.18em] text-zinc-300 hover:text-spidey-neon py-1"
                            data-testid={`nav-mobile-link-${l.id}`}
                        >
                            {l.label}
                        </button>
                    ))}
                    <button
                        onClick={() => go("products")}
                        className="bg-spidey-red text-white text-sm font-bold uppercase tracking-wider px-5 py-3 rounded-full"
                        data-testid="nav-mobile-buy-button"
                    >
                        Acheter maintenant
                    </button>
                </div>
            )}
        </header>
    );
}
